// frontend/src/modules/esign/components/PdfPageCanvas.tsx
//
// Renders one pdf.js page to a canvas at a fixed pixel width, with an
// absolutely-positioned overlay wrapper (`position: relative`) so callers
// can lay field boxes / signature previews on top using ratio coordinates.
// Shared by the field editor (this task) and the signer page (Task 9) — any
// extra props (onClick, onPointerDown, className, data-*...) are forwarded
// straight onto the wrapper div via `ref`, which is how callers hook click
// and drag handling without this component knowing anything about fields.
import * as React from 'react';
import type { PDFDocumentProxy } from '../pdf';

interface PdfPageCanvasProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  width: number;
  children?: React.ReactNode;
  /** Fires once the page's rendered pixel size is known (width fixed, height derived). */
  onSizeChange?: (size: { width: number; height: number }) => void;
}

export const PdfPageCanvas = React.forwardRef<HTMLDivElement, PdfPageCanvasProps>(
  function PdfPageCanvas({ pdf, pageNumber, width, children, onSizeChange, style, ...rest }, ref) {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const [height, setHeight] = React.useState(width * 1.29); // US Letter-ish fallback until page loads

    React.useEffect(() => {
      let cancelled = false;

      pdf.getPage(pageNumber).then((page) => {
        if (cancelled) return;
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = width / baseViewport.width;
        const viewport = page.getViewport({ scale });

        if (!cancelled) {
          setHeight(viewport.height);
          onSizeChange?.({ width, height: viewport.height });
        }

        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');
        // jsdom (unit tests) returns a null 2D context — skip the actual
        // draw there; production browsers always provide one.
        if (!canvas || !context) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        page.render({ canvasContext: context, viewport }).promise.catch(() => {
          /* rendering is best-effort — a failed page shouldn't crash the editor */
        });
      }).catch(() => { /* page failed to load — editor shows an empty canvas */ });

      return () => { cancelled = true; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdf, pageNumber, width]);

    return (
      <div
        ref={ref}
        {...rest}
        style={{ position: 'relative', width, height, ...style }}
      >
        {/* pointerEvents: 'none' — this is a pure paint target. Without it,
            real browsers deliver clicks/pointer events to the canvas (it's
            stacked on top of the wrapper div), not the wrapper — breaking
            any onClick/onPointerDown a caller attaches via `rest`. See
            FieldEditor's handleContainerClick for the consumer side. */}
        <canvas
          ref={canvasRef}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        />
        {children}
      </div>
    );
  },
);
